import { useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { useT } from "../../i18n/LanguageProvider";
import {
  pairPrinter,
  resolvePrinterFor,
  type PrinterRole,
  type ResolvedPrinter,
} from "../../platform/printerRegistry";

/**
 * G-97 — "No printer paired", and the button that fixes it, in ONE place.
 *
 * Owner ruling 2026-09-20, two parts. **Anyone standing in the shop may pair
 * a printer** — florist included. The role check this replaces
 * (`canPairPrinter`, manager and above) guarded nothing: pairing is Bluetooth
 * plus this tablet's `localStorage`, there is no printer model, no endpoint
 * and no permission, and the deleted function's own comment said so
 * ("UI-only: pairing touches no server, so there is nothing to enforce behind
 * it"). A gate that cannot be enforced is not security, it is a florist
 * standing at a counter unable to print.
 *
 * And **every screen that needs a printer offers to pair one**, rather than
 * the note screen offering it while the box label and the item sticker only
 * said so. Those two used to name where to go — one of them pointing at
 * "Printer settings", a screen that does not exist anywhere in this app.
 * Copy that names another screen goes stale; a button cannot.
 *
 * All three surfaces print on the `notes` role, which is why it is the
 * default. The paired printer is handed back through `onPaired` because the
 * two print screens held theirs in state with no setter, so pairing would
 * have appeared to do nothing until the panel was reopened.
 */
export function PairPrinterPrompt({
  role = "notes",
  onPaired,
  disabled = false,
}: {
  role?: PrinterRole;
  onPaired(printer: ResolvedPrinter | null): void;
  disabled?: boolean;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pair = async () => {
    setBusy(true);
    setError(null);
    let paired: ResolvedPrinter | null = null;
    try {
      await pairPrinter(role);
      paired = resolvePrinterFor(role);
    } catch (err: unknown) {
      // `translate`, not the hook's `t`: the words are wanted at the moment
      // the pairing failed, not at the moment this callback was built.
      setError(errorText(err, "printer.couldNotPair"));
      return;
    } finally {
      setBusy(false);
    }
    // OUTSIDE the try on purpose. `onPaired` re-renders the parent — it swaps
    // this prompt for the print controls — and anything that throws in there
    // would otherwise be caught above and shown as "Could not pair the
    // printer", after the printer was paired and written to the registry. The
    // florist would then run the whole Bluetooth chooser again for nothing.
    onPaired(paired);
  };

  return (
    <>
      <div className="sf-note-nodevice">
        <span>
          <strong>{t("printer.nonePaired")}</strong> {t("printer.pairToPrint")}
        </span>
        <button
          type="button"
          className="button"
          onClick={() => void pair()}
          disabled={busy || disabled}
        >
          {busy ? t("printer.waiting") : t("printer.pairAPrinter")}
        </button>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
    </>
  );
}
